import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { showError } from '../../../../../core/alert/sweetAlertConfig';
import { truncateText } from './utils';
import { ItemType, SavedSchemaV2 } from './types';
import ViewItemTypeModal from './ViewItemTypeModal';
import ServiceDetailsPreviewModal from './ServiceDetailsPreviewModal';
import EditItemTypeModal from './EditItemTypeModal';

interface Props {
  loading: boolean;
  types: ItemType[];
  pageNo: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  onEdit: (t: ItemType) => void;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  onViewChecklist: (id: number) => void;
}

interface UpdateItemTypePayload {
  id: number;
  itemType: string;
  description: string;
  checklistTemplate?: string | null;
}


const ItemTypesTable: React.FC<Props> = ({
  loading,
  types,
  pageNo,
  pageSize,
  onPrev,
  onNext,
  onEdit,
  onView,
  onDelete,
  onViewChecklist,
}) => {
  const navigate = useNavigate();
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<SavedSchemaV2 | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItemType, setEditItemType] = useState<ItemType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  // const [addItemType, setAddItemType] = useState<AddItemTypePayload>({
  //   itemType: '',
  //   description: '',
  //   checklistTemplate: null,
  // });
  const [addLoading, setAddLoading] = useState(false);

  const handleView = (itemType: ItemType) => {
    setSelectedItemType(itemType);
    setShowViewModal(true);
  };

  const handleViewChecklist = (itemType: ItemType) => {
    if (itemType.checklistTemplate) {
      try {
        const checklist = JSON.parse(itemType.checklistTemplate) as SavedSchemaV2;
        setSelectedChecklist(checklist);
        setShowChecklistModal(true);
      } catch (e) {
        console.error('Error parsing checklist template:', e);
        showError('Failed to load checklist template due to invalid JSON');
      }
    }
  };

  const handleEdit = (itemType: ItemType) => {
    setEditItemType(itemType);
    setShowEditModal(true);
    onEdit(itemType);
  };

  const handleSaveEdit = async (payload: UpdateItemTypePayload) => {
    try {
      // Assume apiDefinitions.updateItemType exists
      await apiDefinitions.updateItemType(payload);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update item type');
    }
  };



  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="card-title">Item Types</h5>
        <Button
          variant="success"
          size="sm"
          data-bs-toggle="modal"
          data-bs-target="#add_item_type"
          disabled={loading}
        >
          <i className="ti ti-plus me-1" /> Add Item Type
        </Button>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: 160 }}>Checklist</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => {
                  const hasTemplate = !!t.checklistTemplate;
                  return (
                    <tr key={t.id}>
                      <td className="truncate-name">{truncateText(t.itemType, 15)}</td>
                      <td>
                        {hasTemplate ? (
                          <span className="badge bg-success">Template set</span>
                        ) : (
                          <span className="badge bg-secondary">No template</span>
                        )}
                      </td>
                      <td className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(t)}
                          disabled={loading}
                          title="Edit"
                        >
                          <i className="ti ti-edit" />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleView(t)}
                          disabled={loading}
                          title="View details"
                        >
                          <i className="ti ti-eye" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleViewChecklist(t)}
                          disabled={loading || !hasTemplate}
                          title={hasTemplate ? 'View checklist template' : 'No template'}
                        >
                          <i className="ti ti-file-text" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDelete(t.id)}
                          disabled={loading}
                          title="Delete"
                        >
                          <i className="ti ti-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!types.length && (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      No item types found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-footer d-flex justify-content-between">
        <Button
          variant="secondary"
          size="sm"
          onClick={onPrev}
          disabled={pageNo === 1 || loading}
        >
          Previous
        </Button>
        <span>Page {pageNo}</span>
        <Button
          variant="secondary"
          size="sm"
          onClick={onNext}
          disabled={types.length < pageSize || loading}
        >
          Next
        </Button>
      </div>

      <ViewItemTypeModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        itemType={selectedItemType}
        onViewChecklist={onViewChecklist}
      />

      {selectedChecklist && (
        <ServiceDetailsPreviewModal
          show={showChecklistModal}
          onHide={() => setShowChecklistModal(false)}
          serviceDetails={selectedChecklist}
        />
      )}

      {editItemType && (
        <EditItemTypeModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onViewChecklist(0); // Trigger list refresh
          }}
          itemType={editItemType}
          onChange={setEditItemType}
          onSave={handleSaveEdit}
          loading={loading}
        />
      )}

    </div>
  );
};

export default ItemTypesTable;